import { Fragment } from 'react';
import {
  PromoType,
  type PrecheckoutCart,
  type PromosV2,
} from '@hungryroot/rest';
import {
  PromoCodeContainer,
  PromoLineItem,
} from '../PromoLineItem/PromoLineItem';

import styles from '../OrderSummary/OrderSummary.module.css';

interface OrderLineItem {
  label: string;
  value: number;
  id: string;
  code: string;
  type: PromoType;
}

interface PromoValue {
  value: string | null;
  isExpired: boolean;
  type: PromoType;
  label: string;
  code: string;
  threshold: number;
}

const isPromoValid = (
  threshold: number,
  isExpired: boolean,
  subtotal: number,
  type: PromoType
) => {
  return !isExpired && type in PromoType && subtotal >= threshold;
};

const PromoTypeWithDollarEffect = new Set([
  PromoType.FLAT_DISCOUNT,
  PromoType.PERCENT_DISCOUNT,
  PromoType.REFERRAL_DISCOUNT,
]);

function getLabelAndValue(
  { type, effect }: Pick<PromosV2, 'type' | 'effect'>,
  { discount }: { discount: string | null }
) {
  switch (type) {
    case PromoType.FLAT_DISCOUNT:
      return {
        label: effect,
        value: discount,
      };
    case PromoType.FREE_SHIPPING:
      return {
        label: `Shipping`,
        value: null,
      };
    case PromoType.FREE_ITEM:
    case PromoType.FREE_ITEM_FOR_LIFE:
      return {
        label: effect.split('free ')[1],
        value: null,
      };
    case PromoType.REFERRAL_DISCOUNT:
      return {
        label: effect,
        value: discount,
      };
    case PromoType.PERCENT_DISCOUNT:
      return {
        label: effect,
        value: discount,
      };
  }
  return { label: effect, value: null };
}

function getPromos(promos: PromosV2[], discount: string) {
  return promos.map(
    ({ effect, is_expired: isExpired, code, type, threshold }) => {
      return {
        isExpired,
        code,
        type,
        threshold: Number(threshold),
        ...getLabelAndValue({ type, effect }, { discount }),
      };
    },
    []
  );
}

type Props = {
  onPromoSubmitSuccess?: () => void;
  promos: PromosV2[];
  subtotal: string;
  orderId: number;
  shipping: string;
  pricing: Pick<PrecheckoutCart['pricing'], 'discount'>;
};

const getNumPromosWithDollarEffect = (
  subtotal: number,
  promos: PromoValue[]
) => {
  return promos.reduce((acc, promo) => {
    const { threshold, isExpired, type } = promo;
    if (
      PromoTypeWithDollarEffect.has(promo.type) &&
      isPromoValid(Number(threshold), isExpired, subtotal, type)
    ) {
      return acc + 1;
    }
    return acc;
  }, 0);
};

export function OrderSummaryPromoCodes({
  onPromoSubmitSuccess,
  shipping: shippingString,
  orderId,
  promos,
  pricing: { discount: pricingDiscount },
  subtotal: subtotalString,
}: Props) {
  const subtotal = parseFloat(subtotalString);
  const shipping = parseFloat(shippingString);
  const normalizedPromos = getPromos(promos, pricingDiscount);
  const lineItems = normalizedPromos.reduce((acc, promo) => {
    const { value, isExpired, type, label, code, threshold } = promo;
    // skip if expired or type isn't in one of our supported promo types
    if (!isPromoValid(threshold, isExpired, subtotal, type)) {
      return acc;
    }
    acc.push({
      type,
      label,
      value: value === null ? 0 : parseFloat(value),
      code,
      id: label,
    });

    return acc;
  }, [] as OrderLineItem[]);

  const promoCodeInput =
    getNumPromosWithDollarEffect(subtotal, normalizedPromos) === 0 ? (
      <div className={styles.row}>
        <PromoCodeContainer
          onPromoSubmitSuccess={onPromoSubmitSuccess}
          orderId={orderId}
          subtotal={subtotal}
        />
      </div>
    ) : null;

  return (
    <>
      {lineItems.length === 0 && shipping === 0
        ? promoCodeInput
        : lineItems.map(({ label, value, id, code, type }) => {
            return (
              <Fragment key={id}>
                {/* if there's no promo applied, show the promo code input above shipping */}
                {type === PromoType.FREE_SHIPPING && promoCodeInput}
                <div className={styles.row}>
                  <PromoLineItem
                    onPromoSubmitSuccess={onPromoSubmitSuccess}
                    orderId={orderId}
                    value={value}
                    label={label}
                    code={code}
                    type={type}
                    subtotal={subtotal}
                  />
                </div>
              </Fragment>
            );
          })}
      {/* if there's no promo applied, show the promo code input above shipping */}
      {shipping > 0 ? promoCodeInput : null}
    </>
  );
}
